import { SelectionModel } from '@angular/cdk/collections';

import { Component, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { SpinnerComponent } from '@custom-components/general/spinner/spinner.component';
import { FilledButtonComponent, OutlinedButtonComponent } from '@custom-components/general/button';
import { saveAs } from 'file-saver';

import { PAGINATION_DEFAULT, SHIPMENT_DEVICES_COLUMNS_CONFIG } from '@core/constants';
import { ShipmentDetailsModel, ShipmentDeviceDetails } from '@core/models/shipment.model';
import { TranslateModule } from '@ngx-translate/core';
import { CompanyStore } from '@root/app/store/company.store';
import { ShipmentStore } from '@root/app/store/shipment_store';
import { SkeletonBlockComponent } from '@custom-components/general/skeleton/skeleton-block/skeleton-block.component';
import { SkeletonTableComponent } from '@custom-components/general/skeleton/skeleton-table/skeleton-table.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StatusChipComponent } from '@custom-components/general/status-chip/status-chip.component';

@Component({
  selector: 'xemex-shipment-file-detail-dialog',
  imports: [
    MatIconModule,
    MatButtonModule,
    MatTableModule,
    MatCheckboxModule,
    TranslateModule,
    SkeletonBlockComponent,
    SkeletonTableComponent,
    SpinnerComponent,
    StatusChipComponent,
    FilledButtonComponent,
    OutlinedButtonComponent,
  ],
  templateUrl: './shipment-file-detail-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './shipment-file-detail-dialog.component.scss',
})
export class ShipmentFileDetailDialogComponent {
  readonly dialogRef = inject(MatDialogRef<ShipmentFileDetailDialogComponent>);
  shipmentStore = inject(ShipmentStore);
  companyStore = inject(CompanyStore);
  shipmentDetails: ShipmentDetailsModel | undefined;
  isLoadingShipmentDetails = false;
  isLoadingShipmentContent = false;
  data = inject(MAT_DIALOG_DATA);
  snackBar = inject(MatSnackBar);
  columnsConfig = SHIPMENT_DEVICES_COLUMNS_CONFIG;

  displayedColumns: string[] = SHIPMENT_DEVICES_COLUMNS_CONFIG.map(e => e.field);
  dataSource = new MatTableDataSource<ShipmentDeviceDetails>([]);
  pageLength = 0;
  pageSize = PAGINATION_DEFAULT.limit;

  selectionFiles = new SelectionModel<any>(true, []);
  shipmentFileContent: any;

  constructor() {
    if (this.data.shipmentFileId) {
      this.shipmentStore.getShipmentDetails(this.data.shipmentFileId);
    }
    effect(() => {
      this.shipmentDetails = this.shipmentStore.shipmentDetails();
      this.pageLength = this.shipmentDetails?.devices.length ?? 0;
      this.isLoadingShipmentDetails = this.shipmentStore.isLoadingShipmentDetails();
      this.dataSource.data = this.shipmentDetails?.devices ?? [];
      this.isLoadingShipmentContent = this.shipmentStore.isLoadingShipmentContent();
    });

    effect(() => {
      this.shipmentFileContent = this.shipmentStore.shipmentContent();
      this.isLoadingShipmentContent = this.shipmentStore.isLoadingShipmentContent();
      const fileName = this.data.fileDetail.fileName;
      if (this.shipmentFileContent?.length) {
        const blob = new Blob([this.shipmentFileContent], { type: 'text/csv' });
        saveAs(blob, fileName);
      }

      if (this.shipmentStore.getShipmentContentError()) {
        this.snackBar.open('Error downloading Shipment file', 'Close', {
          duration: 3000,
          verticalPosition: 'top',
          horizontalPosition: 'center',
        });
      }
    });
  }

  isAllSelected() {
    const numSelected = this.selectionFiles.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selectionFiles.clear();
      return;
    }

    this.selectionFiles.select(...this.dataSource.data);
  }

  onDownload() {
    this.shipmentStore.getShipmentContent(this.data.shipmentFileId);
  }
}
